import React, { useState, useEffect } from 'react';
import { Table, Input, Button, message, Space, Switch, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import { SearchOutlined, UserAddOutlined } from '@ant-design/icons';
import {     EditOutlined, ZoomInOutlined, DeleteOutlined } from '@ant-design/icons';

import { getAllCustomers, getMyCustomers, deleteCustomer } from '../services/customerService';
import CustomerForm from '../components/CustomerModal/CustomerForm';
import Page from '../components/layout/Page';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import DeleteConfirmationModal from '../components/loan/DeleteConfirmationModal';

const Customers = () => {
    const { t } = useTranslation();
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [showOnlyMyCustomers, setShowOnlyMyCustomers] = useState(true);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchCustomers();
    }, [showOnlyMyCustomers]);

    const fetchCustomers = async () => {
        try {
            setLoading(true);
            const data = showOnlyMyCustomers ? 
                await getMyCustomers() : 
                await getAllCustomers();
            setCustomers(data);
        } catch (error) {
            console.error('Error fetching customers:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            await deleteCustomer(selectedCustomerId);
            message.success(t('customer.delete.success'));
            setIsDeleteModalVisible(false);
            fetchCustomers();
        } catch (error) {
            message.error(t('customer.delete.error'));
        }
    };

    const columns = [
        {
            title: t('common.actions'),
            key: 'actions',
            fixed: 'left',
            width: 150,
            render: (_, record) => (
                <Space size="middle">
                    <Button 
                        onClick={() => navigate(`/customers/${record.id}`)}
                        icon={<ZoomInOutlined />}
                        title="View Details"
                    >
                    </Button>
                    <Button 
                        danger 
                        icon={<DeleteOutlined />} 
                        onClick={() => {
                            setSelectedCustomerId(record.id);
                            setIsDeleteModalVisible(true);
                        }}
                    >
                    </Button>
                </Space>
            ),
        },      
        {
            title: t('customer.fields.name'),
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => <Link to={`/customers/${record.id}`}>{text}</Link>,
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: t('customer.fields.firstName'),  
            dataIndex: 'first_name',
            key: 'first_name',
            render: (text, record) => <Link to={`/customers/${record.id}`}>{text}</Link>,
            sorter: (a, b) => a.name.localeCompare(b.name),
        },        
        {
            title: t('customer.fields.city'),
            dataIndex: 'city',
            key: 'city',
        },        
        {
            title: t('customer.fields.email'),
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: t('customer.fields.phone'),
            dataIndex: 'phone',
            key: 'phone',
        },
    ];

    const headerExtra = (
        <Row gutter={[8, 8]} align="middle">
            <Col xs={12} sm={12} md={12} lg={12}>
                <Input
                    placeholder={t('customer.search')}
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    style={{ width: '100%' }}
                />
            </Col>
            <Col xs={12} sm={12} md={12} lg={12}>
                <Button
                    type="primary"
                    icon={<UserAddOutlined />}
                    onClick={() => setIsModalVisible(true)}
                    style={{ width: '100%' }}
                >
                    {t('customer.addCustomer')}
                </Button>
            </Col>
        </Row>
    );

    const filteredCustomers = customers.filter(customer =>
        (customer.name?.toLowerCase() || '').includes(searchText.toLowerCase()) ||
        (customer.first_name?.toLowerCase() || '').includes(searchText.toLowerCase()) ||
        (customer.city?.toLowerCase() || '').includes(searchText.toLowerCase()) ||
        (customer.street?.toLowerCase() || '').includes(searchText.toLowerCase()) ||
        (customer.phone?.toLowerCase() || '').includes(searchText.toLowerCase()) ||
        (customer.email?.toLowerCase() || '').includes(searchText.toLowerCase())
    );

    const handleEdit = (record) => {
        
        navigate(`/customers/${record.id}`);
    };

    return (
        <Page
            title={t('customer.title')}
            subtitle={t('customer.subtitle')}
            headerExtra={headerExtra}
        >
            <div style={{ 
                width: '100%',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
            }}>

                {/* Filters */}
                <Space style={{ width: '100%', padding: '10px 0px' }}>
                    <Switch
                        defaultChecked
                        checked={showOnlyMyCustomers}
                        onChange={setShowOnlyMyCustomers}
                    />
                    <span>{t('customer.onlyMyCustomers')}</span>
                </Space>
                <Table
                    columns={columns}
                    dataSource={filteredCustomers}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: 'max-content' }}
                />
            </div>

            <CustomerForm
                visible={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onSuccess={() => {
                    setIsModalVisible(false);
                    fetchCustomers();
                }}
            />
            <DeleteConfirmationModal
                visible={isDeleteModalVisible}
                onCancel={() => setIsDeleteModalVisible(false)}
                onConfirm={handleDelete}
            />
        </Page>
    );
};

export default Customers;
